/**
 * Zustellverfolgung ausgehender E-Mails.
 *
 * Der Versand einer E-Mail ist zweistufig: Der Mailprovider nimmt sie zunächst
 * nur an. Ob sie beim Empfänger ankommt, stellt sich erst später heraus und
 * wird per Webhook nachgereicht. Diese Schnittstelle liefert den daraus
 * entstehenden Zustellstatus — insbesondere die Fälle, in denen eine Nachricht
 * NICHT angekommen ist, samt Begründung im Klartext.
 */
import { getRequestGeneric, postRequestGeneric } from "./backendApiGeneric";
import { type DeliveryStatus, isDeliveryProblem } from "./deliveryStatus";

export { isDeliveryProblem };
export type { DeliveryStatus };

/** Gruppierung der Fehlerursache — steuert Symbol und Handlungsempfehlung. */
export type DeliveryFailureType =
  | "invalid_address"
  | "domain_not_found"
  | "mailbox_full"
  | "blocked"
  | "complaint"
  | "attachment_rejected"
  | "temporary"
  | "provider_unreachable"
  | "provider_rejected"
  | "suppressed"
  | "unknown";

/** Ein Zustellversuch an einen Empfänger. */
export interface EmailDelivery {
  id: number;
  provider: string;
  providerMessageId: string | null;
  recipient: string;
  recipientName: string | null;
  recipientRole: "to" | "cc" | "bcc";
  subject: string | null;
  status: DeliveryStatus;
  statusLabel: string;
  failureType: DeliveryFailureType | null;
  failureCode: string | null;
  failureReason: string | null;
  isPermanent: boolean;
  context: string;
  idOrganization: number | null;
  idCase: number | null;
  idConversation: number | null;
  idMessage: number | null;
  idUser: number | null;
  attempts: number;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
  updatedAt: string;
  caseNumber?: string | null;
  caseDescription?: string | null;
  senderName?: string | null;
}

/** Verdichteter Zustellstatus einer Nachricht (über alle Empfänger). */
export interface MessageDeliverySummary {
  idMessage: number;
  total: number;
  delivered: number;
  pending: number;
  /** Angenommen ohne Zustellbestätigung — zählt weder als offen noch als zugestellt. */
  accepted: number;
  failed: number;
  status: DeliveryStatus;
  label: string;
  reason?: string;
  failingRecipients?: string[];
  /** Zeitpunkte für "Zugestellt am … um … Uhr" (Anforderung 01.10.2026, 2.3). */
  sentAt?: string;
  deliveredAt?: string;
  failedAt?: string;
}

export interface ListDeliveriesOptions {
  /** "problems" (Vorgabe) liefert nur Zustellungen, die Aufmerksamkeit brauchen. */
  scope?: "problems" | "all";
  caseId?: number;
  conversationId?: number;
  includeAcknowledged?: boolean;
  limit?: number;
}

/** Lädt Zustellungen der eigenen Organisation, standardmäßig nur die Probleme. */
export async function listDeliveries(
  options: ListDeliveriesOptions = {},
): Promise<EmailDelivery[]> {
  const params = new URLSearchParams();
  if (options.scope) params.set("scope", options.scope);
  if (options.caseId) params.set("caseId", String(options.caseId));
  if (options.conversationId) params.set("conversationId", String(options.conversationId));
  if (options.includeAcknowledged) params.set("includeAcknowledged", "true");
  if (options.limit) params.set("limit", String(options.limit));

  const query = params.toString();
  const result = await getRequestGeneric(
    `/protected/email-deliveries${query ? `?${query}` : ""}`,
  );
  return Array.isArray(result) ? result : [];
}

/** Anzahl offener Zustellprobleme — Grundlage für die Badge am Postausgang. */
export async function getDeliveryProblemCount(): Promise<number> {
  const result = await getRequestGeneric("/protected/email-deliveries/summary");
  return Number(result?.openProblems ?? 0);
}

/**
 * "Status prüfen": gleicht die offenen Zustellungen einer Nachricht sofort mit
 * dem Versanddienst ab und liefert die aktualisierte Zusammenfassung
 * (dasselbe Objekt wie `message.delivery`).
 */
export async function refreshDelivery(idMessage: number): Promise<MessageDeliverySummary> {
  const result = await postRequestGeneric("/protected/email-deliveries/refresh", { idMessage }, true);
  if (!result || typeof result !== "object" || typeof result.idMessage !== "number") {
    throw new Error("Der Zustellstatus konnte nicht geladen werden.");
  }
  return result as MessageDeliverySummary;
}

/** Hakt ein Zustellproblem als bearbeitet ab; es verschwindet aus dem Postausgang. */
export function acknowledgeDelivery(id: number): Promise<any> {
  return postRequestGeneric(`/protected/email-deliveries/${id}/acknowledge`, {}, true);
}

/**
 * Hebt die Sperre einer dauerhaft gescheiterten Adresse auf.
 *
 * Nach einem endgültigen Fehlschlag sendet KARS nicht mehr an diese Adresse.
 * Wurde das Problem beim Empfänger behoben, gibt dieser Aufruf sie wieder frei.
 */
export function unsuppressDelivery(id: number): Promise<any> {
  return postRequestGeneric(`/protected/email-deliveries/${id}/unsuppress`, {}, true);
}

/**
 * Konkrete Handlungsempfehlung zur Fehlerursache.
 *
 * Die Begründung (failureReason) sagt, was passiert ist; dieser Text sagt, was
 * die Kanzlei nun tun kann.
 */
export function deliveryAdvice(failureType: DeliveryFailureType | null): string {
  switch (failureType) {
    case "invalid_address":
    case "domain_not_found":
      return "Adresse im Kontakt korrigieren und Nachricht erneut senden.";
    case "mailbox_full":
      return "Empfänger auf anderem Weg erreichen und später erneut senden.";
    case "blocked":
    case "complaint":
      return "Empfänger telefonisch oder per Post kontaktieren.";
    case "attachment_rejected":
      return "Anhänge verkleinern oder einzeln versenden.";
    case "temporary":
    case "provider_unreachable":
      return "Zustellung wird automatisch wiederholt — bitte später prüfen.";
    case "provider_rejected":
      return "Administrator informieren: Zugang oder Absenderadresse prüfen.";
    case "suppressed":
      return "Adresse ist gesperrt. Nach Korrektur beim Empfänger Sperre aufheben.";
    default:
      return "Nachricht erneut senden oder Empfänger auf anderem Weg erreichen.";
  }
}
